import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import { useAuth } from '../context/AuthContext';
import { hasNavPermission } from '../components/ProtectedRoute';
import { formatISODate } from '../utils/dates';
import { exportRemisionToExcel, exportRemisionToPDF, remisionSheet } from '../utils/remisionSheetExport';
import ExportPreviewModal from '../components/ExportPreviewModal';
import RowProperties from '../components/RowProperties';
import SaleDetails from '../components/SaleDetails';
import SheetPreview from '../components/SheetPreview';
import { isoYearOfDate, useHistoryFilters } from '../components/HistoryFilters';
import HistorySummary from '../components/HistorySummary';
import { REMISIONES_CHANGED_EVENT, fmtBoxes, remisionBoxMismatches } from '../utils/remisionBoxes';
import './CompaniesPage.css';
import './SalesFormPage.css';
import '../styles/rowActions.css';
import '../styles/topbarButtons.css';
import '../styles/pageHeader.css';
import '../styles/priceAlert.css';

// How the shared Historial filters read a remisión (hacienda and exportadora come from its sale).
const FILTER_FIELDS = {
  year: (r) => isoYearOfDate(r.date),
  week: (r) => r.week,
  farm: (r) => r.sale_detail?.farm_name,
  exporters: (r) => [r.sale_detail?.exporter_name],
};

export default function RemisionHistoryPage() {
  const { user } = useAuth();
  const canDelete = hasNavPermission(user, 'delete_records');
  const [remisiones, setRemisiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [exportingId, setExportingId] = useState(null);

  useEffect(() => {
    api.get('dispatch-referrals/')
      .then((r) => setRemisiones(r.data))
      .catch(() => setStatus({ type: 'error', text: 'No se pudo cargar el historial de remisiones.' }))
      .finally(() => setLoading(false));
  }, []);

  const [preview, setPreview] = useState(null); // { rem } | null
  const { filtered, bar: filterBar } = useHistoryFilters(remisiones, FILTER_FIELDS);
  // Sales whose remisiones don't add up to the sale's Cajas; "Ver solo esas" (?descuadre=1) lists
  // their remisiones, whatever the filters.
  const mismatches = remisionBoxMismatches(remisiones);
  const [searchParams, setSearchParams] = useSearchParams();
  const onlyMismatched = searchParams.get('descuadre') === '1';
  const setOnlyMismatched = (on) => setSearchParams(on ? { descuadre: '1' } : {}, { replace: true });
  const rows = onlyMismatched ? remisiones.filter((r) => mismatches.has(r.sale)) : filtered;
  // The remisión's own cajas / pallets (pre-filled from the sale, editable on the remisión).
  const summary = [
    ['Total cajas', rows.reduce((total, r) => total + (r.boxes || 0), 0)],
    ['Total pallets', rows.reduce((total, r) => total + (r.pallets || 0), 0)],
    ['Remisiones registradas', rows.length],
  ];

  const handleExcel = async (rem) => {
    setExportingId(rem.id);
    setStatus(null);
    try {
      await exportRemisionToExcel(rem);
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo exportar a Excel.' });
    } finally {
      setExportingId(null);
    }
  };

  const handlePDF = (rem) => {
    setStatus(null);
    try {
      exportRemisionToPDF(rem);
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo exportar a PDF.' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta remisión?')) return;
    setDeletingId(id);
    setStatus(null);
    try {
      await api.delete(`dispatch-referrals/${id}/`);
      setRemisiones((xs) => xs.filter((x) => x.id !== id));
      window.dispatchEvent(new Event(REMISIONES_CHANGED_EVENT));
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudo eliminar la remisión.') });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="companies-root">
      <div className="sales-form-topbar">
        <Link to="/despachos" className="topbar-btn">← Despachos</Link>
        <Link to="/despachos/remision" className="topbar-btn topbar-btn-primary">+ Nueva remisión</Link>
      </div>
      <h1 className="companies-title">📋 Historial de Remisiones</h1>
      <p className="page-subtitle page-header-divided">Una remisión por despacho. Ábrela para seguir editando, o elimínala desde aquí.</p>

      {status && <div className={`companies-status companies-status-${status.type}`}>{status.text}</div>}

      {(mismatches.size > 0 || onlyMismatched) && (
        <div className="price-alert" role="alert">
          <span>
            {onlyMismatched
              ? `Mostrando las remisiones de ${mismatches.size} ${mismatches.size === 1 ? 'venta' : 'ventas'} cuyas cajas no suman lo vendido (sin filtros).`
              : `⚠️ ${mismatches.size} ${mismatches.size === 1 ? 'venta tiene' : 'ventas tienen'} remisiones cuyas cajas no suman lo vendido.`}
          </span>
          <button type="button" className="price-alert-btn" onClick={() => setOnlyMismatched(!onlyMismatched)}>
            {onlyMismatched ? 'Ver todas' : 'Ver solo esas'}
          </button>
        </div>
      )}

      {!onlyMismatched && filterBar}

      <HistorySummary cards={summary} />

      <div className="companies-card">
        {loading ? (
          <div className="companies-empty">Cargando…</div>
        ) : remisiones.length === 0 ? (
          <div className="companies-empty">Aún no hay remisiones registradas.</div>
        ) : rows.length === 0 ? (
          <div className="companies-empty">Sin remisiones para los filtros seleccionados.</div>
        ) : (
          <table className="companies-table sales-history-table">
            <thead>
              <tr>
                <th className="sales-id-col">ID</th>
                <th>Turno</th>
                <th>Fecha</th>
                <th>Hacienda</th>
                <th>Destino</th>
                <th>Conductor</th>
                <th>Contenedor</th>
                <th>Tipo de caja</th>
                <th className="num">Cajas</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = r.sale_detail || {};
                const mismatch = mismatches.get(r.sale);
                return (
                  <tr
                    key={r.id}
                    title={r.created_by_username ? `Registrado por ${r.created_by_username}` : undefined}
                  >
                    <td className="sales-id-col sales-id">{r.id}</td>
                    <td>{r.title}</td>
                    <td>
                      <div>{formatISODate(r.date)}</div>
                      <div className="sales-sub">Sem {r.week} · {r.day}</div>
                    </td>
                    <td>
                      {/* The hacienda centered over its sale's chip. */}
                      <div className="remision-farm-cell">
                        <div>{s.farm_name}</div>
                        <SaleDetails sale={s} />
                      </div>
                    </td>
                    <td>{r.destination_name || '—'}</td>
                    <td>
                      <div>{r.driver_name}</div>
                      <div className="sales-sub">{r.driver_license_plate || '—'}</div>
                    </td>
                    <td>{r.container_number}</td>
                    <td>{s.box_type_name || '—'}</td>
                    <td className="num">
                      <div>{r.boxes?.toLocaleString('es-EC')}</div>
                      {mismatch && (
                        <div
                          className="remision-boxes-warning"
                          title={`Venta #${r.sale}: ${fmtBoxes(mismatch.sale_boxes)} cajas vendidas; sus ${mismatch.remisiones.length} `
                            + `${mismatch.remisiones.length === 1 ? 'remisión suma' : 'remisiones suman'} ${fmtBoxes(mismatch.remision_boxes)} `
                            + `(remisiones #${mismatch.remisiones.join(', #')}).`}
                        >
                          ⚠️ {fmtBoxes(mismatch.remision_boxes)} de {fmtBoxes(mismatch.sale_boxes)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        <Link
                          className="row-icon-btn"
                          to={`/despachos/remision/${r.id}`}
                          title="Editar"
                          aria-label="Editar"
                        >
                          ✏️
                        </Link>
                        <button
                          className="row-icon-btn"
                          onClick={() => setPreview({ rem: r })}
                          disabled={exportingId === r.id}
                          title="Exportar (Excel o PDF)"
                          aria-label="Exportar"
                        >
                          📤
                        </button>
                        <RowProperties item={r} title={`${r.title} · ${r.sale_detail?.farm_name || ''}`} />
                        {canDelete && (
                          <button
                            className="row-icon-btn"
                            onClick={() => handleDelete(r.id)}
                            disabled={deletingId === r.id}
                            title="Eliminar"
                            aria-label="Eliminar"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {preview && (
        <ExportPreviewModal
          title={`${preview.rem.title} · ${preview.rem.sale_detail?.farm_name || ''}`}
          fileName={remisionSheet(preview.rem).fileBase}
          onClose={() => setPreview(null)}
          onDownload={(fmt) => (fmt === 'excel' ? handleExcel(preview.rem) : handlePDF(preview.rem))}
        >
          <SheetPreview sheet={remisionSheet(preview.rem)} />
        </ExportPreviewModal>
      )}
    </div>
  );
}
